<template>
    <!-- Detalhes -->
    <div class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div class="flex items-center justify-between border-b border-gray-100 px-5 py-3 dark:border-gray-700/50">
            <h3 class="text-sm font-medium text-gray-900 dark:text-white">Detalhes</h3>
        </div>
        <dl class="divide-y divide-gray-100 dark:divide-gray-700/50">
            <div class="grid grid-cols-3 gap-4 px-5 py-3">
                <dt class="text-xs text-gray-500 dark:text-gray-400">Tipo de Conta</dt>
                <dd class="col-span-2 text-sm text-gray-900 dark:text-white">{{ account_type }}</dd>
            </div>
            <div class="grid grid-cols-3 gap-4 px-5 py-3">
                <dt class="text-xs text-gray-500 dark:text-gray-400">Nome</dt>
                <dd class="col-span-2 text-sm text-gray-900 dark:text-white">{{ user.name }}</dd>
            </div>
            <div class="grid grid-cols-3 gap-4 px-5 py-3">
                <dt class="text-xs text-gray-500 dark:text-gray-400">Email</dt>
                <dd class="col-span-2 flex items-center text-sm text-gray-900 dark:text-white">
                    <span class="grow">{{ user.email }}</span>
                    <button
                        v-if="user?.user_email_logs?.length"
                        type="button"
                        class="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-gray-600"
                        @click="openModalLogs = true"
                    >
                        Logs ({{ user?.user_email_logs?.length }})
                    </button>
                </dd>
            </div>
            <div class="grid grid-cols-3 gap-4 px-5 py-3">
                <dt class="text-xs text-gray-500 dark:text-gray-400">{{ user.type === "company" ? "CNPJ" : "CPF" }}</dt>
                <dd class="col-span-2 font-mono text-sm text-gray-900 dark:text-white">
                    {{ user.type === "company" ? formatCnpj(user.cnpj) : formatCpf(user.cpf) }}
                </dd>
            </div>
            <div v-if="user.about" class="grid grid-cols-3 gap-4 px-5 py-3">
                <dt class="text-xs text-gray-500 dark:text-gray-400">About</dt>
                <dd class="col-span-2 text-sm text-gray-900 dark:text-white">{{ user.about }}</dd>
            </div>
            <div v-if="user.website" class="grid grid-cols-3 gap-4 px-5 py-3">
                <dt class="text-xs text-gray-500 dark:text-gray-400">Website</dt>
                <dd class="col-span-2 text-sm text-gray-900 dark:text-white">{{ user.website }}</dd>
            </div>
            <template v-for="address in user.addresses" :key="address.id">
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">
                        Endereço
                        <span class="block text-[10px] text-gray-400 dark:text-gray-500">
                            {{ address.updated_at || address.created_at }}
                        </span>
                    </dt>
                    <dd class="col-span-2 text-sm text-gray-900 dark:text-white">
                        {{ address.street }}, n° {{ address.street_number }} -
                        {{ address.neighborhood }} -
                        {{ address.city }}/{{ address.state }} -
                        {{ address.zip_code }}
                    </dd>
                </div>
            </template>
            <template v-for="phone in user.phones" :key="phone.id">
                <div class="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt class="text-xs text-gray-500 dark:text-gray-400">
                        Telefone · {{ phoneTypeLabel(phone.type) }}{{ isSmsPhone(phone) ? " (SMS)" : "" }}
                        <span class="block text-[10px] text-gray-400 dark:text-gray-500">
                            {{ phone.updated_at || phone.created_at }}
                        </span>
                    </dt>
                    <dd class="col-span-2 flex items-center text-sm text-gray-900 dark:text-white">
                        <span class="grow font-mono">
                            {{ phone.ddi }} ({{ phone.ddd }}) {{ formatPhone(phone.number) }}
                        </span>
                        <button
                            v-if="phone?.user_phone_logs?.length"
                            type="button"
                            class="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-gray-600"
                            @click="selectedPhone = phone; openModalPhoneLogs = true"
                        >
                            Logs ({{ phone?.user_phone_logs?.length }})
                        </button>
                    </dd>
                </div>
            </template>
            <div class="grid grid-cols-3 gap-4 px-5 py-3">
                <dt class="text-xs text-gray-500 dark:text-gray-400">Gerente de Conta</dt>
                <dd class="col-span-2 text-sm text-gray-900 dark:text-white">{{ account_manager }}</dd>
            </div>
            <div class="grid grid-cols-3 gap-4 px-5 py-3">
                <dt class="text-xs text-gray-500 dark:text-gray-400">UTM do Cadastro</dt>
                <dd class="col-span-2 text-sm text-gray-900 dark:text-white">{{ query_params_created }}</dd>
            </div>
        </dl>
    </div>

    <!-- Configurações -->
    <div class="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div class="border-b border-gray-100 px-5 py-3 dark:border-gray-700/50">
            <h3 class="text-sm font-medium text-gray-900 dark:text-white">Configurações</h3>
        </div>
        <div class="divide-y divide-gray-100 dark:divide-gray-700/50">
            <div class="flex items-center justify-between px-5 py-3">
                <span class="text-xs text-gray-500 dark:text-gray-400">Selo de Verificado</span>
                <form-toggle v-model="is_verified" :loading="loading" />
            </div>
            <div class="flex items-center justify-between px-5 py-3">
                <span class="text-xs text-gray-500 dark:text-gray-400">Afiliação com 100% de comissão</span>
                <form-toggle v-model="allow_integral_commission" :loading="loading" />
            </div>
            <div class="flex items-center justify-between px-5 py-3">
                <span class="text-xs text-gray-500 dark:text-gray-400">Pix Automático</span>
                <form-toggle v-model="allow_pix_recurring_payment" :loading="loading" />
            </div>
            <div class="flex items-center justify-between px-5 py-3">
                <span class="text-xs text-gray-500 dark:text-gray-400">Pix Automático - Novo Provider</span>
                <form-toggle v-model="allow_pix_recurring_new_provider_system" :loading="loading" />
            </div>
            <div class="flex items-center justify-between px-5 py-3">
                <span class="text-xs text-gray-500 dark:text-gray-400">Flow 2.0</span>
                <form-toggle v-model="allow_flow_v2" :loading="loading" />
            </div>
            <div class="flex items-center justify-between px-5 py-3">
                <span class="text-xs text-gray-500 dark:text-gray-400">Apple Pay</span>
                <form-toggle v-model="allow_apple_pay" :loading="loading" />
            </div>
            <div class="flex items-center justify-between px-5 py-3">
                <span class="text-xs text-gray-500 dark:text-gray-400">CRM Bonus (cashback ao comprador, cobrado por venda)</span>
                <form-toggle v-model="crm_bonus_enabled" :loading="loading" />
            </div>
            <div class="flex items-center justify-between px-5 py-3">
                <div>
                    <span class="text-xs text-gray-500 dark:text-gray-400">CRM Bonus: taxa por venda negociada (R$)</span>
                    <p class="text-[11px] text-gray-400 dark:text-gray-500">Vazio usa a taxa global de Serviços &gt; CRM Bonus.</p>
                </div>
                <input
                    v-model="crm_bonus_price"
                    type="text"
                    inputmode="decimal"
                    placeholder="Taxa global"
                    :disabled="loading"
                    class="w-28 rounded-md border border-gray-300 bg-white px-2 py-1 text-right text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                />
            </div>
            <div class="flex items-center justify-between px-5 py-3">
                <div>
                    <span class="text-xs text-gray-500 dark:text-gray-400">CRM Bonus: envio do cashback (dias após a compra)</span>
                    <p class="text-[11px] text-gray-400 dark:text-gray-500">Vazio usa o prazo de Serviços &gt; CRM Bonus. 0 envia na hora.</p>
                </div>
                <input
                    v-model="crm_bonus_release_delay_days"
                    type="text"
                    inputmode="numeric"
                    placeholder="Prazo do plano"
                    :disabled="loading"
                    class="w-28 rounded-md border border-gray-300 bg-white px-2 py-1 text-right text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                />
            </div>
            <div class="px-5 py-3">
                <div class="flex items-center justify-between">
                    <div>
                        <span class="text-xs text-gray-500 dark:text-gray-400">Notificação WhatsApp</span>
                        <span
                            v-if="iris_enabled"
                            class="ml-2 rounded-full px-2 py-0.5 text-[11px] font-medium"
                            :class="
                                irisEnabledCount === irisNotificationTypes.length
                                    ? 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-300'
                                    : 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300'
                            "
                        >
                            {{ irisEnabledCount }} de {{ irisNotificationTypes.length }} tipos ligados
                        </span>
                    </div>
                    <form-toggle
                        :modelValue="iris_enabled"
                        @update:modelValue="onIrisEnabledChange"
                        :loading="loading"
                    />
                </div>

                <p v-if="!iris_enabled" class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                    Desligada: os 4 tipos ficam desligados e este seller não recebe nenhuma notificação. Ao ligar, todos voltam ligados e você escolhe quais manter.
                </p>

                <div
                    v-else
                    class="mt-3 divide-y divide-gray-100 rounded-md border border-gray-200 dark:divide-gray-700/50 dark:border-gray-700"
                >
                    <div
                        v-for="type in irisNotificationTypes"
                        :key="type.key"
                        class="flex items-center justify-between gap-4 px-3 py-2"
                    >
                        <div class="min-w-0">
                            <p class="text-xs font-medium text-gray-700 dark:text-gray-200">
                                {{ type.label }}
                                <span
                                    class="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-normal text-gray-500 dark:bg-gray-700 dark:text-gray-300"
                                >
                                    {{ type.origin }}
                                </span>
                            </p>
                            <p class="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
                                {{ type.description }}
                            </p>
                        </div>
                        <div class="flex shrink-0 items-center gap-2">
                            <span
                                class="text-[11px] font-medium"
                                :class="
                                    iris_notifications[type.key]
                                        ? 'text-green-600 dark:text-green-400'
                                        : 'text-gray-400 dark:text-gray-500'
                                "
                            >
                                {{ iris_notifications[type.key] ? "Ligado" : "Desligado" }}
                            </span>
                            <form-toggle
                                v-model="iris_notifications[type.key]"
                                :loading="loading"
                                class="!mb-0"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="px-5 py-3">
                <div class="flex items-center justify-between">
                    <span class="text-xs text-gray-500 dark:text-gray-400">Produtor Selected</span>
                    <form-toggle v-model="is_producer_selected" :loading="loading" />
                </div>
                <div class="mt-2 flex justify-end">
                    <button
                        type="button"
                        class="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-gray-600"
                        @click="openProducerSelectedHistory"
                    >
                        Histórico
                    </button>
                </div>
            </div>
        </div>
        <div class="border-t border-gray-100 px-5 py-3 dark:border-gray-700/50">
            <button
                type="button"
                :disabled="loading"
                class="rounded-md bg-gray-900 px-4 py-2 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
                @click.prevent="submit"
            >
                <svg v-if="loading" class="-ml-0.5 mr-1.5 inline h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Salvar
            </button>
        </div>
    </div>
    <producer-experience-section :user="user" class="mt-4" />
    <modal :open="openModalLogs" @close="openModalLogs = false" size="4xl">
        <table-component
            :loading="false"
            :headers="['Email', 'Alterado por:', 'Motivo', 'Data de Alteração']"
        >
            <tr v-for="log in user?.user_email_logs" :key="log.id">
                <td class="table-cell">
                    <p>de: {{ log.old_email }}</p>
                    <p>para: {{ log.new_email }}</p>
                </td>
                <td class="table-cell">
                    <p>{{ log.changed_by_user.name }}</p>
                    <p>{{ log.changed_by_user.email }}</p>
                </td>
                <td class="table-cell">{{ log.reason }}</td>
                <td class="table-cell">{{ formatDate(log.created_at) }}</td>
            </tr>
        </table-component>
    </modal>
    <modal :open="openModalProducerSelectedHistory" @close="openModalProducerSelectedHistory = false" size="4xl">
        <table-component
            :loading="loadingProducerSelectedHistory"
            :headers="['Ação', 'Alterado por', 'Data']"
        >
            <tr v-if="producerSelectedHistory.length === 0">
                <td class="table-cell text-center" colspan="3">
                    Nenhuma alteração registrada.
                </td>
            </tr>
            <tr v-for="entry in producerSelectedHistory" :key="entry.id">
                <td class="table-cell">
                    <span
                        v-if="entry.new_value"
                        class="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200"
                    >
                        Ativou
                    </span>
                    <span
                        v-else
                        class="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-200"
                    >
                        Desativou
                    </span>
                </td>
                <td class="table-cell">
                    <p v-if="entry.changed_by">{{ entry.changed_by.name }}</p>
                    <p v-if="entry.changed_by" class="text-xs text-gray-500 dark:text-gray-400">
                        {{ entry.changed_by.email }}
                    </p>
                    <p v-else class="text-xs text-gray-500 dark:text-gray-400">—</p>
                </td>
                <td class="table-cell">{{ formatDate(entry.created_at) }}</td>
            </tr>
        </table-component>
    </modal>
    <modal :open="openModalPhoneLogs" @close="openModalPhoneLogs = false" size="4xl">
        <table-component
            :loading="false"
            :headers="['Telefone', 'Alterado por:', 'Motivo', 'Data de Alteração']"
        >
            <tr v-for="log in selectedPhone?.user_phone_logs" :key="log.id">
                <td class="table-cell">
                    <p>de: {{ log.old_phone }}</p>
                    <p>para: {{ log.new_phone }}</p>
                </td>
                <td class="table-cell">
                    <p>{{ log.changed_by_user?.name }}</p>
                    <p>{{ log.changed_by_user?.email }}</p>
                </td>
                <td class="table-cell">{{ log.reason }}</td>
                <td class="table-cell">{{ formatDate(log.created_at) }}</td>
            </tr>
        </table-component>
    </modal>
</template>

<script>
import { PaperClipIcon } from "@heroicons/vue/24/outline";
import CostActions from "../../../components/costs/CostActions.vue";
import FormToggle from "../../../components/forms/FormToggle.vue";
import SubmitButton from "../../../components/forms/SubmitButton.vue";
import Modal from "../../../components/layouts/Modal.vue";
import TableComponent from "../../../components/layouts/table/TableComponent.vue";
import HelperMixin from "../../../mixins/HelperMixin.vue";
import ProducerExperienceSection from "./ProducerExperienceSection.vue";
import { getSmsPhone } from "../../../utils/phones.js";
// O suporte precisa ver qual registro é o celular: é o único que o "Alterar Telefone" troca (TIC2-16047).
const PHONE_TYPE_LABELS = {
    mobile: "Celular",
    whatsapp: "WhatsApp",
    landline: "Fixo",
};
// Mesmos event_types do enum App\Enum\Iris\IrisNotificationType (Payments, Kraken, Mozart API).
const IRIS_NOTIFICATION_TYPES = [
    {
        key: "pix_gerado",
        label: "Pix Gerado",
        origin: "Payments",
        description: "QR Code na hora em que o PIX do checkout é gerado.",
    },
    {
        key: "pagamento_pendente",
        label: "Pagamento Pendente",
        origin: "Payments",
        description: "Lembrete com magic link se o PIX continuar sem pagamento.",
    },
    {
        key: "acesso_ebook",
        label: "Acesso a E-book",
        origin: "Kraken",
        description: "Link de download quando a compra de um e-book é aprovada.",
    },
    {
        key: "acesso_mozart",
        label: "Acesso ao Mozart",
        origin: "Mozart",
        description: "Primeiro acesso à área de membros quando o acesso é criado.",
    },
];

const IRIS_NOTIFICATION_DEFAULTS = Object.fromEntries(
    IRIS_NOTIFICATION_TYPES.map((type) => [type.key, true]),
);

export default {
    props: ["user"],
    components: {
        CostActions,
        TableComponent,
        Modal,
        PaperClipIcon,
        FormToggle,
        SubmitButton,
        ProducerExperienceSection,
    },
    mixins: [HelperMixin],

    data() {
        return {
            openModalLogs: false,
            openModalPhoneLogs: false,
            selectedPhone: null,
            loading: false,
            is_verified: false,
            phones: [],
            addresses: [],
            queryParams: null,
            userEmailLogs: [],
            allow_integral_commission: false,
            allow_pix_recurring_payment: false,
            allow_pix_recurring_new_provider_system: false,
            allow_flow_v2: false,
            allow_apple_pay: false,
            is_producer_selected: false,
            iris_enabled: false,
            crm_bonus_enabled: false,
            // Em reais, como digitado; "" = usa a taxa global.
            crm_bonus_price: "",
            // Em dias, como digitado; "" = usa o prazo do plano.
            crm_bonus_release_delay_days: "",
            iris_notifications: { ...IRIS_NOTIFICATION_DEFAULTS },
            irisNotificationTypes: IRIS_NOTIFICATION_TYPES,
            openModalProducerSelectedHistory: false,
            loadingProducerSelectedHistory: false,
            producerSelectedHistory: [],
            account_type: "",
            account_manager: "",
            query_params_created: "",
        };
    },

    computed: {
        irisEnabledCount() {
            return IRIS_NOTIFICATION_TYPES.filter(
                (type) => this.iris_notifications[type.key],
            ).length;
        },
    },

    mounted() {
        this.fetch();
    },

    methods: {
        phoneTypeLabel(type) {
            return PHONE_TYPE_LABELS[type] ?? "Sem tipo";
        },
        isSmsPhone(phone) {
            return getSmsPhone(this.user.phones)?.id === phone.id;
        },
        fetch() {
            this.loading = true;
            axios
                .get("/api/user/" + this.user.id)
                .then(({ data }) => {
                    console.info(data);
                    this.is_verified = data.setting?.is_verified;
                    this.phones = data.phones;
                    this.addresses = data.addresses;
                    this.userEmailLogs = data.user_email_logs;
                    this.allow_integral_commission =
                        data.setting?.allow_integral_commission;
                    this.allow_pix_recurring_payment =
                        data.setting?.allow_pix_recurring_payment;
                    this.allow_pix_recurring_new_provider_system =
                        data.setting?.allow_pix_recurring_new_provider_system;
                    this.allow_flow_v2 = data.setting?.allow_flow_v2 ?? false;
                    this.allow_apple_pay =
                        data.setting?.allow_apple_pay;
                    this.is_producer_selected = data.is_producer_selected ?? false;
                    this.iris_enabled = data.setting?.iris_enabled ?? false;
                    this.crm_bonus_enabled = data.setting?.crm_bonus_enabled ?? false;
                    this.crm_bonus_price =
                        data.setting?.crm_bonus_price == null
                            ? ""
                            : (data.setting.crm_bonus_price / 100).toFixed(2).replace(".", ",");
                    this.crm_bonus_release_delay_days =
                        data.setting?.crm_bonus_release_delay_days == null
                            ? ""
                            : String(data.setting.crm_bonus_release_delay_days);
                    // Sem JSON salvo (seller do modelo antigo) todos os tipos valem como ligados.
                    this.iris_notifications = {
                        ...IRIS_NOTIFICATION_DEFAULTS,
                        ...(data.setting?.iris_notifications || {}),
                    };
                    this.account_type = data.account_type;
                    this.account_manager = data.account_manager;
                    this.query_params_created = data.query_params_created;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        // Religar pela tela volta ao padrão (todos ligados); o fetch() não passa por aqui.
        onIrisEnabledChange(enabled) {
            this.iris_enabled = enabled;

            if (enabled) {
                this.iris_notifications = { ...IRIS_NOTIFICATION_DEFAULTS };
            }
        },
        openProducerSelectedHistory() {
            this.openModalProducerSelectedHistory = true;
            this.loadingProducerSelectedHistory = true;
            axios
                .get(`/api/user/${this.user.id}/producer-selected/history`)
                .then(({ data }) => {
                    this.producerSelectedHistory = data.history ?? [];
                })
                .catch(({ response }) => {
                    this.$notify(
                        {
                            group: "error",
                            title: "Erro",
                            text: response?.data?.message ?? "Falha ao carregar histórico",
                        },
                        4000,
                    );
                })
                .finally(() => {
                    this.loadingProducerSelectedHistory = false;
                });
        },
        crmBonusPriceInCents() {
            const value = String(this.crm_bonus_price ?? "").trim();

            if (value === "") {
                return null;
            }

            // "1.234,56" ou "2,50" (padrao BR); sem virgula, o ponto e o separador decimal ("2.50").
            const normalized = value.includes(",") ? value.replace(/\./g, "").replace(",", ".") : value;
            const cents = Math.round(Number(normalized) * 100);

            // Valor invalido vai como texto e o backend devolve o erro de validacao.
            return Number.isFinite(cents) ? cents : value;
        },
        crmBonusReleaseDelayDays() {
            const value = String(this.crm_bonus_release_delay_days ?? "").trim();

            if (value === "") {
                return null;
            }

            // Valor invalido vai como texto e o backend devolve o erro de validacao.
            return /^\d+$/.test(value) ? Number(value) : value;
        },
        submit() {
            this.loading = true;
            const payload = {
                is_verified: this.is_verified,
                allow_integral_commission: this.allow_integral_commission,
                allow_pix_recurring_payment: this.allow_pix_recurring_payment,
                allow_pix_recurring_new_provider_system: this.allow_pix_recurring_new_provider_system,
                allow_flow_v2: this.allow_flow_v2,
                allow_apple_pay: this.allow_apple_pay,
                is_producer_selected: this.is_producer_selected,
                iris_enabled: this.iris_enabled,
                crm_bonus_enabled: this.crm_bonus_enabled,
                crm_bonus_price: this.crmBonusPriceInCents(),
                crm_bonus_release_delay_days: this.crmBonusReleaseDelayDays(),
            };

            // Mestre desligado = os 4 tipos desligados (o backend garante o mesmo).
            payload.iris_notifications = this.iris_enabled
                ? this.iris_notifications
                : Object.fromEntries(
                      IRIS_NOTIFICATION_TYPES.map((type) => [type.key, false]),
                  );

            axios
                .put("/api/user/" + this.user.id + "/isVerified", payload)
                .then(({ data }) => {
                    this.loading = false;
                })
                .catch(({ response }) => {
                    this.loading = false;
                    this.is_producer = !this.is_producer;
                    this.$notify(
                        {
                            group: "error",
                            title: "Error",
                            text: response.data.message,
                        },
                        4000,
                    );
                });
        },
    },
};
</script>
